import ResponsePicker from './ResponsePicker';

const LOT_SIZE_UNITS   = ['Sq Ft', 'Sq M', 'Acres', 'Hectares', 'Condo Unit Factor', 'Strat Unit Entitlement'];
const ZONING_SOURCES   = [
  'Contacted Municipal Authority',
  'Contacted Provincial Planning Authority',
  'MLS Data',
  'Online (specify)',
  'Online municipal zoning map',
];
const EASEMENTS        = ['Assumed Typical', 'Unknown'];
const DETRIMENTAL_SITE = [
  'Evidence of natural disaster',
  'Other (specify)',
  'Storage of debris/drums/metal/tires',
  'Underground fuel storage/tank',
];
const LAND_VALUE_SRCS  = ['Appraisers Database', 'MLS', 'Online Data', 'Other (specify)'];
const HBU_LAND_COMMENT = ['See Cost Approach addendum', 'See Narrative addendum'];
const LANDSCAPING_OPT  = ['Good', 'Average', 'Fair', 'Poor/Other'];

const HBU_DEF =
  'The reasonably probable use of real property, that is physically possible, legally permissible, ' +
  'financially feasible, maximally productive and that results in the highest value. (CUSPAP)';

export default function SiteTab({ site, onChange }) {
  const set   = (k, v) => onChange({ ...site, [k]: v });
  const setU  = (k, v) => onChange({ ...site, utilities:    { ...site.utilities,    [k]: v } });
  const setWS = (k, v) => onChange({ ...site, water_supply: { ...site.water_supply, [k]: v } });
  const setFt = (k, v) => onChange({ ...site, features:     { ...site.features,     [k]: v } });
  const setEl = (k, v) => onChange({ ...site, electrical:   { ...site.electrical,   [k]: v } });
  const setDr = (k, v) => onChange({ ...site, driveway:     { ...site.driveway,     [k]: v } });
  const setPk = (k, v) => onChange({ ...site, parking:      { ...site.parking,      [k]: v } });

  const appendHBU = (text) => {
    const cur = site.hbu_comments || '';
    set('hbu_comments', cur ? `${cur}\n\n${text}` : text);
  };
  const appendComment = (text) => {
    const cur = site.comments || '';
    set('comments', cur ? `${cur}\n\n${text}` : text);
  };

  return (
    <div className="rt-body">

      {/* ── A: SITE DETAILS ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Site Details</h3>

        <div className="rt-row">
          <div className="rt-field rt-field-wide">
            <label>Site Dimensions</label>
            <input type="text" value={site.dimensions ?? ''}
              placeholder="e.g. 50 × 120 ft"
              onChange={e => set('dimensions', e.target.value)} />
          </div>
          <div className="rt-field">
            <label>Source</label>
            <input type="text" value={site.source ?? ''}
              placeholder="GeoWarehouse, Survey…"
              onChange={e => set('source', e.target.value)} />
          </div>
        </div>

        <div className="rt-row">
          <div className="rt-field">
            <label>Lot Size</label>
            <input type="text" value={site.lot_size ?? ''}
              onChange={e => set('lot_size', e.target.value)} />
          </div>
          <div className="rt-field">
            <label>Unit</label>
            <select value={site.lot_size_unit ?? 'Sq Ft'}
              onChange={e => set('lot_size_unit', e.target.value)}>
              {LOT_SIZE_UNITS.map(u => <option key={u}>{u}</option>)}
            </select>
          </div>
          <div className="rt-field">
            <label>Topography</label>
            <input type="text" value={site.topography ?? ''}
              placeholder="e.g. Level, Sloping…"
              onChange={e => set('topography', e.target.value)} />
          </div>
          <div className="rt-field">
            <label>Configuration</label>
            <input type="text" value={site.configuration ?? ''}
              placeholder="e.g. Rectangular, Corner…"
              onChange={e => set('configuration', e.target.value)} />
          </div>
        </div>
      </div>

      {/* ── B: ZONING ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Zoning</h3>

        <div className="rt-row">
          <div className="rt-field rt-field-wide">
            <label>Zoning Code / Description</label>
            <input type="text" value={site.zoning_code ?? ''}
              onChange={e => set('zoning_code', e.target.value)} />
          </div>
          <div className="rt-field">
            <label>Zoning Source</label>
            <select value={site.zoning_source ?? ''}
              onChange={e => set('zoning_source', e.target.value)}>
              <option value="">Select…</option>
              {ZONING_SOURCES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="rt-field">
          <label>Other Land Use Controls</label>
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            <label className="rt-check">
              <input type="radio" checked={site.other_land_use_controls === true}
                onChange={() => set('other_land_use_controls', true)} />
              Yes
            </label>
            <label className="rt-check">
              <input type="radio" checked={site.other_land_use_controls === false}
                onChange={() => set('other_land_use_controls', false)} />
              No
            </label>
          </div>
          {site.other_land_use_controls === true && (
            <input type="text" style={{ marginTop: 8 }}
              value={site.other_land_use_text ?? ''}
              placeholder="Describe…"
              onChange={e => set('other_land_use_text', e.target.value)} />
          )}
        </div>

        <div className="rt-field" style={{ marginTop: 12 }}>
          <label>Existing Land Use Conforms to Zoning</label>
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            <label className="rt-check">
              <input type="radio" checked={site.existing_use_conforms === true}
                onChange={() => set('existing_use_conforms', true)} />
              Yes
            </label>
            <label className="rt-check">
              <input type="radio" checked={site.existing_use_conforms === false}
                onChange={() => set('existing_use_conforms', false)} />
              No
            </label>
          </div>
          {site.existing_use_conforms === false && (
            <input type="text" style={{ marginTop: 8 }}
              value={site.existing_use_text ?? ''}
              placeholder="See comments…"
              onChange={e => set('existing_use_text', e.target.value)} />
          )}
        </div>

        <div className="rt-field" style={{ marginTop: 12 }}>
          <label>In Floodplain / Flood Zone</label>
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            <label className="rt-check">
              <input type="radio" checked={site.in_floodplain === true}
                onChange={() => set('in_floodplain', true)} />
              Yes
            </label>
            <label className="rt-check">
              <input type="radio" checked={site.in_floodplain === false}
                onChange={() => set('in_floodplain', false)} />
              No
            </label>
            <label className="rt-check" style={{ marginLeft: 16 }}>
              <input type="checkbox" checked={site.floodplain_unknown ?? false}
                onChange={e => set('floodplain_unknown', e.target.checked)} />
              Other comment / Unknown
            </label>
          </div>
          {site.in_floodplain === true && (
            <div className="rt-field" style={{ marginTop: 8 }}>
              <label>Flood Map Date</label>
              <input type="date" value={site.flood_map_date || ''}
                onChange={e => set('flood_map_date', e.target.value)} />
            </div>
          )}
        </div>

        <div className="rt-field" style={{ marginTop: 12 }}>
          <label>Easements</label>
          <select value={site.easements ?? 'Assumed Typical'}
            onChange={e => set('easements', e.target.value)}>
            {EASEMENTS.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>

        <div className="rt-field" style={{ marginTop: 12 }}>
          <label className="rt-check">
            <input type="checkbox" checked={site.detrimental_observed ?? false}
              onChange={e => {
                set('detrimental_observed', e.target.checked);
                if (!e.target.checked) set('detrimental_type', '');
              }} />
            Detrimental Conditions Observed
          </label>
          {site.detrimental_observed && (
            <div style={{ marginTop: 8 }}>
              <select value={site.detrimental_type ?? ''}
                onChange={e => set('detrimental_type', e.target.value)}>
                <option value="">Select…</option>
                {DETRIMENTAL_SITE.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ── C: UTILITIES ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Services &amp; Utilities</h3>

        <div className="rt-sub-label">Utilities</div>
        <div className="rt-check-group" style={{ marginBottom: 16 }}>
          {[
            ['natural_gas',    'Natural Gas'],
            ['storm_sewer',    'Storm Sewer'],
            ['sanitary_sewer', 'Sanitary Sewer'],
            ['open_ditch',     'Open Ditch'],
            ['septic',         'Septic'],
            ['holding_tank',   'Holding Tank'],
          ].map(([key, label]) => (
            <label key={key} className="rt-check">
              <input type="checkbox" checked={site.utilities?.[key] ?? false}
                onChange={e => setU(key, e.target.checked)} />
              {label}
            </label>
          ))}
        </div>

        <div className="rt-sub-label">Water Supply</div>
        <div className="rt-check-group" style={{ marginBottom: 16 }}>
          {[['municipal', 'Municipal'], ['private_well', 'Private Well']].map(([key, label]) => (
            <label key={key} className="rt-check">
              <input type="checkbox" checked={site.water_supply?.[key] ?? false}
                onChange={e => setWS(key, e.target.checked)} />
              {label}
            </label>
          ))}
          <div className="rt-field" style={{ flex: '0 0 180px' }}>
            <input type="text" value={site.water_supply?.other ?? ''}
              placeholder="Other…"
              onChange={e => setWS('other', e.target.value)} />
          </div>
        </div>

        <div className="rt-sub-label">Street Features</div>
        <div className="rt-check-group" style={{ marginBottom: 16 }}>
          {[
            ['gravel_road',  'Gravel Road'],
            ['paved_road',   'Paved Road'],
            ['lane',         'Lane'],
            ['sidewalk',     'Sidewalk'],
            ['curbs',        'Curbs'],
            ['streetlights', 'Streetlights'],
          ].map(([key, label]) => (
            <label key={key} className="rt-check">
              <input type="checkbox" checked={site.features?.[key] ?? false}
                onChange={e => setFt(key, e.target.checked)} />
              {label}
            </label>
          ))}
          <div className="rt-field" style={{ flex: '0 0 180px' }}>
            <input type="text" value={site.features?.other ?? ''}
              placeholder="Other…"
              onChange={e => setFt('other', e.target.value)} />
          </div>
        </div>

        <div className="rt-sub-label">Electrical</div>
        <div className="rt-check-group">
          {[['overhead', 'Overhead'], ['underground', 'Underground'], ['other', 'Other']].map(([key, label]) => (
            <label key={key} className="rt-check">
              <input type="checkbox" checked={site.electrical?.[key] ?? false}
                onChange={e => setEl(key, e.target.checked)} />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* ── D: ACCESS ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Access</h3>

        <div className="rt-sub-label">Driveway</div>
        <div className="rt-check-group" style={{ marginBottom: 16 }}>
          {[
            ['private', 'Private'], ['shared', 'Shared'], ['none', 'None'],
            ['single', 'Single'], ['double', 'Double'],
            ['underground', 'Underground'], ['laneway', 'Laneway'],
          ].map(([key, label]) => (
            <label key={key} className="rt-check">
              <input type="checkbox" checked={site.driveway?.[key] ?? false}
                onChange={e => setDr(key, e.target.checked)} />
              {label}
            </label>
          ))}
          <div className="rt-field" style={{ flex: '0 0 160px' }}>
            <input type="text" value={site.driveway?.other ?? ''}
              placeholder="Other…"
              onChange={e => setDr('other', e.target.value)} />
          </div>
        </div>

        <div className="rt-sub-label">Parking</div>
        <div className="rt-check-group" style={{ marginBottom: 16 }}>
          {[
            ['garage', 'Garage'], ['carport', 'Carport'], ['driveway', 'Driveway'],
            ['street', 'Street'], ['underground', 'Underground'],
          ].map(([key, label]) => (
            <label key={key} className="rt-check">
              <input type="checkbox" checked={site.parking?.[key] ?? false}
                onChange={e => setPk(key, e.target.checked)} />
              {label}
            </label>
          ))}
          <div className="rt-field" style={{ flex: '0 0 160px' }}>
            <input type="text" value={site.parking?.other ?? ''}
              placeholder="Other…"
              onChange={e => setPk('other', e.target.value)} />
          </div>
        </div>

        <div className="rt-sub-label">Landscaping</div>
        <div className="rt-check-group">
          {LANDSCAPING_OPT.map(opt => (
            <label key={opt} className="rt-check">
              <input type="radio"
                checked={(site.landscaping?.[opt.toLowerCase().replace('/', '_')] ?? false)}
                onChange={() => {
                  const updated = LANDSCAPING_OPT.reduce((acc, o) => ({
                    ...acc, [o.toLowerCase().replace('/', '_')]: o === opt,
                  }), {});
                  onChange({ ...site, landscaping: updated });
                }} />
              {opt}
            </label>
          ))}
        </div>
      </div>

      {/* ── E: SITE COMMENTS ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Site Comments</h3>

        <div className="rt-field">
          <label>Site Improvements</label>
          <textarea rows={3} value={site.site_improvements ?? ''}
            placeholder="Deck, shed, pool, retaining wall…"
            onChange={e => set('site_improvements', e.target.value)} />
        </div>

        <div className="rt-field" style={{ marginTop: 10 }}>
          <div className="rt-field-label-row">
            <label>Comments</label>
            <ResponsePicker category="site" onInsert={appendComment} />
          </div>
          <textarea rows={6} value={site.comments ?? ''}
            onChange={e => set('comments', e.target.value)} />
        </div>
      </div>

      {/* ── F: HIGHEST & BEST USE ── */}
      <div className="rt-section">
        <h3 className="rt-section-title">Highest &amp; Best Use</h3>

        <div className="rt-field">
          <label>Existing Use</label>
          <input type="text" value={site.existing_use ?? ''}
            onChange={e => set('existing_use', e.target.value)} />
        </div>

        <div className="rt-field" style={{ marginTop: 12 }}>
          <label>HBU — Land as if Vacant</label>
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            <label className="rt-check">
              <input type="radio"
                checked={site.hbu_vacant !== 'Other (specify)' && !!site.hbu_vacant}
                onChange={() => set('hbu_vacant', 'Residential')} />
              Residential
            </label>
            <label className="rt-check">
              <input type="radio" checked={site.hbu_vacant === 'Other (specify)'}
                onChange={() => set('hbu_vacant', 'Other (specify)')} />
              Other
            </label>
          </div>
          {site.hbu_vacant === 'Other (specify)' && (
            <input type="text" style={{ marginTop: 8 }}
              value={site.hbu_vacant_other ?? ''}
              onChange={e => set('hbu_vacant_other', e.target.value)} />
          )}
        </div>

        <div className="rt-field" style={{ marginTop: 12 }}>
          <label>HBU — Land as Improved</label>
          <div className="rt-check-group" style={{ marginTop: 4 }}>
            <label className="rt-check">
              <input type="radio"
                checked={site.hbu_improved !== 'Other (specify)' && !!site.hbu_improved}
                onChange={() => set('hbu_improved', 'Existing Residential Use')} />
              Existing Residential Use
            </label>
            <label className="rt-check">
              <input type="radio" checked={site.hbu_improved === 'Other (specify)'}
                onChange={() => set('hbu_improved', 'Other (specify)')} />
              Other
            </label>
          </div>
          {site.hbu_improved === 'Other (specify)' && (
            <input type="text" style={{ marginTop: 8 }}
              value={site.hbu_improved_other ?? ''}
              onChange={e => set('hbu_improved_other', e.target.value)} />
          )}
        </div>

        <div className="rt-field" style={{ marginTop: 12 }}>
          <div className="rt-field-label-row">
            <label>HBU Comments / Permitted Uses</label>
            <ResponsePicker category="hbu" onInsert={appendHBU} />
          </div>
          <textarea rows={4} value={site.hbu_comments ?? ''}
            onChange={e => set('hbu_comments', e.target.value)} />
        </div>

        <div className="rt-field" style={{ marginTop: 10 }}>
          <label>HBU Land Comment</label>
          <select value={site.hbu_land_comment ?? ''}
            onChange={e => set('hbu_land_comment', e.target.value)}>
            <option value="">Select…</option>
            {HBU_LAND_COMMENT.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>

        <hr className="rt-divider" />

        <div className="rt-row">
          <div className="rt-field">
            <label>Land Value as if Vacant</label>
            <div className="rt-prefix-input">
              <span className="rt-prefix">$</span>
              <input type="text" value={site.land_value ?? ''}
                disabled={site.land_value_na}
                onChange={e => set('land_value', e.target.value)} />
            </div>
          </div>
          <div className="rt-field rt-field-auto">
            <label>&nbsp;</label>
            <label className="rt-check">
              <input type="checkbox" checked={site.land_value_na ?? true}
                onChange={e => set('land_value_na', e.target.checked)} />
              N/A
            </label>
          </div>
          <div className="rt-field">
            <label>Source of Data</label>
            <select value={site.land_value_source ?? ''}
              onChange={e => set('land_value_source', e.target.value)}>
              <option value="">Select…</option>
              {LAND_VALUE_SRCS.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="rt-field">
          <label>Land Value Comment</label>
          <input type="text" value={site.land_value_comment ?? ''}
            onChange={e => set('land_value_comment', e.target.value)} />
        </div>

        <div className="rt-field" style={{ marginTop: 16, padding: '10px 14px', background: '#f8f9fb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Definition of Highest &amp; Best Use</div>
          <div style={{ fontSize: 13, color: '#374151', fontStyle: 'italic', lineHeight: 1.5 }}>{HBU_DEF}</div>
        </div>
      </div>

    </div>
  );
}
